import { Button, FormControl, Typography, styled } from '@ori-ui/mui';
import { useApiClient, createCustomerDocument, deleteCustomerDocument } from '@ori/global-api-client-react';
import type {
  CreateCustomerDocumentBody,
  CreateCustomerDocumentParams,
  GlobalSharedValidationErrorsResponse,
} from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useMemo, useRef } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useNotificationData } from '../../contexts';
import { useCustomHeaders } from '../../hooks';
import type { IDocumentsInfoStoreData } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';
import { useCustomerDocumentsInfos, useRegistrationData } from '../../models/api';
import { getErrorLocalizationByCode } from '../../utils';
import { Loading } from '../Loading';
import { FileInputField } from './FileInputField';
import type { IFileUploadResult } from './IFileUploadResult';
import type { IUploadResult, IUploaderProps } from './IUploadResult';

const bytesInKb = 1024;

const RootContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

const UploadButtonWrapper = styled('div')(({ theme }) => ({
  textAlign: 'center',
}));

const UploadButton = styled(Button)(({ theme }) => ({
  maxWidth: '330px',
  width: '100%',
}));

const triggerFileUploadFinished = (
  result: IFileUploadResult,
  onFileUploadedProp?: (fileUploadResult: IFileUploadResult) => void,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy
  if (onFileUploadedProp && result) {
    onFileUploadedProp(result);
  }
};

const mapToFileUploadResult = (
  response: Partial<GlobalSharedValidationErrorsResponse> | number,
  file: File,
  fileKey: number,
) => {
  let fileUploadResult: IFileUploadResult = {
    file,
    fileKey,
    isSuccess: false,
  };

  if (typeof response === 'number') {
    fileUploadResult = {
      ...fileUploadResult,
      documentId: response,
      isSuccess: true,
    };
  } else if (response.errors?.length) {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/no-unnecessary-condition -- needed this condition
    const code = response && response.errors && response.errors[0] && response.errors[0].code;
    const message = getErrorLocalizationByCode(code);

    fileUploadResult = {
      ...fileUploadResult,
      message: (
        <Localization
          id={message.id}
          defaultMessage={message.defaultMessage}
        />
      ),
    };
  } else {
    fileUploadResult = {
      ...fileUploadResult,
      message: response.message,
    };
  }

  return fileUploadResult;
};

const maxFileSizeError = (maxFileSizeProp?: number, file?: File) => {
  if (maxFileSizeProp && file && file.size > maxFileSizeProp) {
    return true;
  }

  return false;
};

export const UploaderForm: FC<IUploaderProps> = (props) => {
  const {
    accept,
    isMultiple,
    children,
    handleFileChanged,
    handleUploadStart: onUploadStart,
    handleFileUploaded: onFileUploaded,
    handleUploadFinished: onUploadFinished,
    maxFileSize,
    documentTypeId,
    isTemporary,
    isRequired,
    inputName,
  } = props;

  const inputRef = useRef<HTMLInputElement | null>();

  const { getTestIdProps } = useTestId();
  const { data: registrationData } = useRegistrationData();

  const { data: documentsInfoData } = useCustomerDocumentsInfos();

  const customerId = registrationData?.customerId ?? null;

  const headers = useCustomHeaders();

  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;

  const api = useApiClient();

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const getInputRef = (element: HTMLInputElement | null): void => {
    inputRef.current = element;
    if (props.inputRef) {
      props.inputRef(element);
    }
  };

  const createDocument = async (
    customerIdProperty: number,
    requestBody: CreateCustomerDocumentBody,
    requestOptions: CreateCustomerDocumentParams,
  ): Promise<FetchResult<GlobalSharedValidationErrorsResponse | number>> => {
    try {
      const response = await createCustomerDocument(api, customerIdProperty, requestBody, requestOptions, headers);

      return new FetchResult<GlobalSharedValidationErrorsResponse | number>(
        response.status,

        response.data,
      );
    } catch (error: unknown) {
      setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotCreate}
            defaultMessage={defaultTranslations.cannotCreate}
          />
        ),
        type: MessageType.error,
      });
    }

    return FetchResult.error;
  };

  const deleteDocument = async (customerIdProperty: number, documentId: number): Promise<FetchResult> => {
    try {
      const response = await deleteCustomerDocument(api, customerIdProperty, documentId, headers);
      const result = new FetchResult<unknown>(response.status);

      return result;
    } catch (error: unknown) {
      setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotDelete}
            defaultMessage={defaultTranslations.cannotDelete}
          />
        ),
        type: MessageType.error,
      });
    }

    return FetchResult.error;
  };

  const replaceDocument = async (
    customerIdProperty: number,
    oldDocumentId: number,
    requestBody: CreateCustomerDocumentBody,
    newDocumentRequestOptions: CreateCustomerDocumentParams,
  ): Promise<FetchResult<GlobalSharedValidationErrorsResponse | number>> => {
    let deleteResult: FetchResult;

    try {
      deleteResult = await deleteDocument(customerIdProperty, oldDocumentId);

      if (deleteResult.isSuccess()) {
        return await createDocument(customerIdProperty, requestBody, newDocumentRequestOptions);
      }

      return FetchResult.error;
    } catch (error: unknown) {
      setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotCreate}
            defaultMessage={defaultTranslations.cannotCreate}
          />
        ),
        type: MessageType.error,
      });
    }

    return FetchResult.error;
  };

  const button = (
    <UploadButton
      fullWidth
      variant="outlined"
      color="primary"
      {...getTestIdProps('uploader-form-btn-upload')}
    >
      <Localization
        id={TranslationKey.ThirdStep_SelectFile}
        defaultMessage={defaultTranslations.ThirdStep_SelectFile}
      />
    </UploadButton>
  );

  const requestOptions: CreateCustomerDocumentParams = useMemo(
    () => ({
      documentTypeId,
      isTemporary: isTemporary === undefined ? true : isTemporary,
    }),
    [documentTypeId, isTemporary],
  );

  const [lastOfType] = useMemo(
    () =>
      documentsInfoData
        ?.filter((info) => {
          const isTemporaryDocument = false;

          return info.documentTypeId === documentTypeId && (!info.isTemporary || isTemporaryDocument);
        })
        .sort((a, b) => {
          if (a.createdDate.getTime() && b.createdDate.getTime()) {
            return a.createdDate.getTime() - b.createdDate.getTime();
          }

          return 0;
        }) ?? ([] as IDocumentsInfoStoreData[]),
    [documentTypeId, documentsInfoData],
  );

  const fileMapper = async (file: File, key: number, customerIdProperty: number, result: IUploadResult) => {
    const negativeOne = -1;
    const fileKey = (key + 1) * negativeOne;

    requestOptions.documentTypeId = documentTypeId;

    if (maxFileSize && maxFileSizeError(maxFileSize, file)) {
      const maxMBs = Math.ceil(maxFileSize / bytesInKb / bytesInKb);
      const errorMessage = (
        <Localization
          id={TranslationKey.selectedFileExceedsMaximumSize}
          defaultMessage={defaultTranslations.selectedFileExceedsMaximumSize}
          values={{ maxMBs }}
        />
      );

      const fileResult: IFileUploadResult = {
        file,
        fileKey,
        isSuccess: false,
        message: errorMessage,
      };

      triggerFileUploadFinished(fileResult, onFileUploaded);

      return;
    }

    let fetchResult: FetchResult<GlobalSharedValidationErrorsResponse | number>;
    if (isTemporary === false && lastOfType?.documentId && !isMultiple) {
      fetchResult = await replaceDocument(customerIdProperty, lastOfType.documentId, { file }, requestOptions);
    } else {
      fetchResult = await createDocument(customerIdProperty, { file }, requestOptions);
    }

    if (fetchResult.response) {
      const fileResult = mapToFileUploadResult(fetchResult.response, file, fileKey);
      triggerFileUploadFinished(fileResult, onFileUploaded);
      result.uploadedFiles.push(fileResult);
    }
  };

  const handleChangeFile = async (
    files: FileList,
    field: ControllerRenderProps<FieldValues, string>,
    customerIdProperty: number,
  ) => {
    if (handleFileChanged && files[0] !== undefined) {
      handleFileChanged(files[0]);
    }

    if (files.length > 0 && onUploadStart) {
      onUploadStart(files);
    }

    const result: IUploadResult = { uploadedFiles: [] };
    // eslint-disable-next-line unicorn/prefer-spread -- we want this writing
    const uploadPromises = Array.from(files).map(async (file, key) =>
      fileMapper(file, key, customerIdProperty, result),
    );

    await Promise.all(uploadPromises);

    if (onUploadFinished) {
      field.onChange(result.uploadedFiles);
      onUploadFinished(result);
    }
  };

  return (
    <RootContainer>
      <Controller
        name={inputName}
        rules={{
          required: isRequired,
        }}
        render={({ formState, field }) => (
          <FormControl>
            <FileInputField
              name={inputName}
              accept={accept}
              inputRef={getInputRef}
              isMultiple={isMultiple}
              onChange={(e) => {
                const { files } = e.target as HTMLInputElement;

                if (files?.length && customerId) {
                  handleChangeFile(files, field, customerId);
                }
              }}
            />
            {formState.isSubmitting ? <Loading /> : null}
          </FormControl>
        )}
      />
      <UploadButtonWrapper onClick={handleClick}>{children ?? button}</UploadButtonWrapper>
      <Typography
        variant="caption"
        color="textSecondary"
        textAlign="center"
        display="block"
      >
        <Localization
          id={TranslationKey.ThirdStep_DocumentFileSize}
          defaultMessage={defaultTranslations.ThirdStep_DocumentFileSize}
        />
      </Typography>
    </RootContainer>
  );
};
